import * as Sentry from '@sentry/browser';

import { classifyOperationError } from '../data/errors';
import {
  OperationAbortedError,
  type MutationContext,
  type MutationLane,
} from '../data/mutationLane';
import { isOperationAllowed } from '../model/mutationPolicy';
import {
  createOpKey,
  type OpKey,
  type PendingOp,
  type TemplatePermissions,
} from '../model/operations';
import type { CustomizationAction } from '../state/actions';
import type { CustomizationState } from '../state/customizationState';
import type { Message } from '../viewmodels/messages';
import type { Notifier } from './useNotifier';

export type OperationSpec = {
  op: PendingOp;
  execute: (context: MutationContext) => Promise<number>;
  successMessage: Message;
};

export type RunOperationDeps = {
  lane: MutationLane;
  getLatestState: () => CustomizationState;
  dispatch: (action: CustomizationAction) => void;
  notifier: Notifier;
  permissions: TemplatePermissions;
  isMounted: () => boolean;
};

type Settled = { status: 'succeeded' } | { status: 'failed'; error: unknown };

async function settle(
  lane: MutationLane,
  templateId: string,
  task: OperationSpec['execute']
): Promise<Settled> {
  try {
    await lane.enqueue(templateId, task);

    return { status: 'succeeded' };
  } catch (error) {
    return { status: 'failed', error };
  }
}

type SettledFailure = {
  opKey: OpKey;
  op: PendingOp;
  error: unknown;
};

function handleFailure(
  { opKey, op, error }: SettledFailure,
  { getLatestState, dispatch, notifier }: RunOperationDeps
): void {
  // The lane already refused this op's work, so nothing ran and nothing needs reporting
  if (error instanceof OperationAbortedError) {
    dispatch({ type: 'OP_ABORTED', opKey });

    return;
  }

  const failure = classifyOperationError(error);
  const { templateId } = getLatestState();

  Sentry.captureException(error, {
    tags: { operation: op.kind, outcome: failure.outcome },
    extra: { templateId, targetId: op.targetId },
  });

  dispatch({ type: 'OP_FAILED', opKey, failure });

  if (failure.outcome === 'unknown') {
    return;
  }

  notifier.notifyFailure();

  // The lane aborts every queued successor before their own results are handled, so
  // they are still pending here. They are the ops dropped behind this failure
  const droppedCount = Object.keys(getLatestState().pendingOps).length;

  if (droppedCount > 0) {
    notifier.notifyChangesDropped(droppedCount);
  }
}

export async function runOperation(
  { op, execute, successMessage }: OperationSpec,
  deps: RunOperationDeps
): Promise<void> {
  const { lane, getLatestState, dispatch, notifier, permissions, isMounted } =
    deps;
  const { templateId, pendingOps, staleReason } = getLatestState();

  const verdict = isOperationAllowed(op.kind, op.targetId, {
    pendingOps,
    staleReason,
    permissions,
  });

  if (!verdict.allowed) {
    return;
  }

  const opKey = createOpKey(op.kind, op.targetId);

  dispatch({ type: 'OP_STARTED', op });

  // The confirmation runs inside the lane task, so the next queued write starts
  // after the replica holds the version this one produced
  const confirmingTask: OperationSpec['execute'] = async context => {
    const newVersion = await execute(context);

    if (isMounted()) {
      dispatch({ type: 'OP_SUCCEEDED', opKey, newVersion });
    }

    return newVersion;
  };

  const settled = await settle(lane, templateId, confirmingTask);

  if (!isMounted()) {
    return;
  }

  if (settled.status === 'failed') {
    handleFailure({ opKey, op, error: settled.error }, deps);

    return;
  }

  notifier.notifySuccess(successMessage);
}
